/**
 * PushNotificationToggle - UI component for managing push notifications
 */
import React, { useState } from 'react';
import { Bell, BellOff, BellRing, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

export function PushNotificationToggle({ variant = 'card' }) {
  const { language } = useLanguage();
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  } = usePushNotifications();
  
  const [testLoading, setTestLoading] = useState(false);

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success(language === 'pt-BR' 
          ? 'Notificações push desativadas' 
          : 'Push notifications disabled');
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success(language === 'pt-BR' 
          ? 'Notificações push ativadas!' 
          : 'Push notifications enabled!');
      }
    }
  };

  const handleTest = async () => {
    setTestLoading(true);
    const success = await sendTest();
    setTestLoading(false);
    
    if (success) {
      toast.info(language === 'pt-BR' 
        ? 'Notificação de teste enviada!' 
        : 'Test notification sent!');
    } else {
      toast.error(language === 'pt-BR' 
        ? 'Erro ao enviar teste' 
        : 'Error sending test');
    }
  };

  // Not supported message
  if (!isSupported) {
    return variant === 'card' ? (
      <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3 text-yellow-700 dark:text-yellow-400">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">
              {language === 'pt-BR' 
                ? 'Seu navegador não suporta notificações push' 
                : 'Your browser does not support push notifications'}
            </p>
          </div>
        </CardContent>
      </Card>
    ) : (
      <div className="text-sm text-yellow-600 dark:text-yellow-400 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        <span>
          {language === 'pt-BR' 
            ? 'Push não suportado' 
            : 'Push not supported'}
        </span>
      </div>
    );
  }

  // Permission denied message
  if (permission === 'denied') {
    return variant === 'card' ? (
      <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
            <BellOff className="w-5 h-5" />
            <div>
              <p className="text-sm font-medium">
                {language === 'pt-BR' 
                  ? 'Notificações bloqueadas' 
                  : 'Notifications blocked'}
              </p>
              <p className="text-xs mt-1 opacity-80">
                {language === 'pt-BR' 
                  ? 'Permita notificações nas configurações do navegador' 
                  : 'Allow notifications in browser settings'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    ) : (
      <div className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
        <BellOff className="w-4 h-4" />
        <span>
          {language === 'pt-BR' 
            ? 'Notificações bloqueadas' 
            : 'Notifications blocked'}
        </span>
      </div>
    );
  }

  // Card variant (for settings page)
  if (variant === 'card') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isSubscribed ? (
              <BellRing className="w-5 h-5 text-green-500" />
            ) : (
              <Bell className="w-5 h-5 text-slate-500" />
            )}
            {language === 'pt-BR' ? 'Notificações Push' : 'Push Notifications'}
          </CardTitle>
          <CardDescription>
            {language === 'pt-BR' 
              ? 'Receba alertas mesmo com o app fechado' 
              : 'Receive alerts even when the app is closed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isSubscribed ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <Bell className="w-4 h-4 text-slate-400" />
              )}
              <span className="text-sm">
                {isSubscribed 
                  ? (language === 'pt-BR' ? 'Ativadas' : 'Enabled')
                  : (language === 'pt-BR' ? 'Desativadas' : 'Disabled')
                }
              </span>
            </div>
            <Switch
              checked={isSubscribed}
              onCheckedChange={handleToggle}
              disabled={loading}
              data-testid="push-notification-toggle"
            />
          </div>
          
          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
          
          {isSubscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTest}
              disabled={testLoading}
              className="w-full"
              data-testid="push-test-button"
            >
              {testLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BellRing className="w-4 h-4 mr-2" />
              )}
              {language === 'pt-BR' ? 'Enviar teste' : 'Send test'}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Simple toggle variant (for header/dropdown)
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isSubscribed ? (
          <BellRing className="w-4 h-4 text-green-500" />
        ) : (
          <Bell className="w-4 h-4 text-slate-400" />
        )}
        <span className="text-sm">
          {language === 'pt-BR' ? 'Push' : 'Push'}
        </span>
      </div>
      <Switch
        checked={isSubscribed}
        onCheckedChange={handleToggle}
        disabled={loading}
        data-testid="push-notification-toggle-simple"
      />
    </div>
  );
}

export default PushNotificationToggle;
